import React, { useCallback, useEffect, useReducer } from 'react'
import { Popup } from '../../../../design_system/components/Popup/Popup.jsx'
import reducer from '../../../utils/CommonReducer'
import { useSelector } from 'react-redux'
import { SelectionControl } from '../../../../design_system/components/SelectionControls/SelectionControl.jsx'
import { Link } from 'react-router-dom/cjs/react-router-dom.min'
import Avatar from '../../../../design_system/components/Avatar/Avatar.jsx'
import { Tooltip } from '../../../../design_system/components/ToolTip/ToolTip.jsx'
import { map_and_post_job } from '../../../admin_views/new_temp_mapper/api'
import ConditionalRender from '../../../../ConditionalRender'
import AnMultipleSuggestionInputBox from '../../../input_utils/AnMultipleSuggestionBox'
import { SuggestionTags } from '../../../admin_views/new_temp_mapper/_SuggestionTags'


const MapAndPostJobPopup = ({ selectedJobUpdate = {}, onPublishCallback, source = 'job_updates', ...props }) => {
    const current_user = useSelector(state => state.current_user.user);
    const [componentData, setComponentData] = useReducer(reducer, {
        isLoading: {},
        selectedSuggestion: "",
        manualProspect: [],
        manualProspectEnabled: false,
        createNewProspect: false,
        errors: {}
    })
    const { selectedSuggestion, manualProspect, manualProspectEnabled, createNewProspect } = componentData
    const { prospect_suggestions: prospectSuggestions = [] } = selectedJobUpdate
    const selectedProspect = prospectSuggestions.find(prospect => prospect.id === selectedSuggestion) || manualProspect[0]
    const allSuggestionsDisabled = prospectSuggestions.length > 0 && prospectSuggestions.every(prospect => prospect.job_updates_disabled)
    const actionLabel = !createNewProspect && (selectedProspect?.job_updates_disabled || (!selectedProspect && allSuggestionsDisabled)) ? "Map" : "Map and Save"

    const setComponentState = useCallback((payload) => {
        setComponentData({ type: "SET_STATE", payload })
    }, [])

    useEffect(() => {
        if (manualProspectEnabled) setComponentState({ selectedSuggestion: "", createNewProspect: false })
    }, [manualProspectEnabled, setComponentState])

    useEffect(() => {
        if (selectedSuggestion) setComponentState({ manualProspect: [], createNewProspect: false, manualProspectEnabled: false })
    }, [selectedSuggestion, setComponentState])

    useEffect(() => {
        if (createNewProspect) setComponentState({ selectedSuggestion: "", manualProspect: [], manualProspectEnabled: false })
    }, [createNewProspect, setComponentState])

    const mapAndPostSubmit = () => {
        let errors = {}
        if (!selectedSuggestion && !(manualProspect?.length > 0) && !createNewProspect) {
            errors.postingJob = "Please select a suggestion or enter a prospect id or create new prospect"
        }

        if (Object.keys(errors).length > 0) {
            setComponentState({ errors: errors });
            return
        }
        setComponentState({ isLoading: { postingJob: true }, errors: errors })
        map_and_post_job({
            post_id: selectedJobUpdate.id,
            prospect_id: selectedSuggestion || manualProspect[0]?.id,
            create_new_prospect: createNewProspect,
            // The render site knows the surface; the server emits 'Prospect Mapped' with it.
            source: source
        }).then(res => {
            if (res.data.success) {
                setComponentState({ isLoading: {} })
                // 'Prospect Mapped' is emitted server-side by TempPostController#map, so it is
                // recorded even when a tracker is blocked.
                onPublishCallback(selectedJobUpdate.id, res.data)
            } else {
                setComponentState({
                    isLoading: {},
                    errors: { postingJob: "Something went wrong!" }
                })
            }
        }).catch(err => {
            // 404 means the job update is already gone server-side - clear the row instead of
            // leaving one that can only ever 404 again.
            if (err.response?.status === 404) {
                onPublishCallback(selectedJobUpdate.id, { mapped: false, gone: true })
                return
            }
            setComponentState({
                isLoading: {},
                errors: { postingJob: err.response?.data?.error || "Something went wrong!" }
            })
        })
    }

    return (
        <Popup
            name={actionLabel}
            handleOnClosePopup={props.onCloseCallback}
            maxWidth={580}
            primaryAction={{ label: actionLabel, onClick: mapAndPostSubmit, isLoading: componentData.isLoading.postingJob }}
        >
            {prospectSuggestions.length > 0 ? prospectSuggestions.map((prospect, index) => {
                return (
                    <React.Fragment key={prospect.id}>
                        <div className="map-popup__prospect-row flex justify-between items-center" style={{ marginTop: index > 0 ? 'var(--space-2xl)' : 0 }}>
                            <div className="map-popup__prospect-inner flex items-center">
                                <SelectionControl
                                    type="radio"
                                    label=""
                                    checked={selectedSuggestion === prospect.id}
                                    onChange={() => setComponentState({ selectedSuggestion: prospect.id })}
                                />
                                <div className="map-popup__prospect-detail-wrap">
                                    <Link to={{ pathname: `/prospects/${prospect.id}` }}
                                        className="map-popup__prospect-link flex items-start no-underline"
                                    >
                                        <Avatar
                                            width={40}
                                            name={prospect.name}
                                            profile_image={prospect.profile_image}
                                        />
                                        <div className="map-popup__avatar-detail">
                                            <div className="map-popup__name-row flex items-center relative">
                                                <div className='body-l-bold'>{prospect.name}</div>
                                                <Tooltip
                                                    tooltipText={() => prospect.tags?.map((tag, i) => <span key={tag} className="map-popup__tag-label">{tag.name}{((i + 1) !== prospect.tags?.length) ? (i + 1) % 3 === 0 ? <br /> : ", " : ""}</span>)}
                                                    placement='bottom'
                                                >
                                                    <div className="truncate map-popup__tags-row">
                                                        {prospect.tags?.map((tag) => <span key={tag.id} className="body-s-semibold map-popup__tag-item">{tag.name}</span>)}
                                                    </div>
                                                </Tooltip>
                                            </div>
                                            <Tooltip
                                                tooltipText={() => prospect.designation}
                                                placement='bottom-start'
                                            >
                                                <div className="body-m-semibold truncate map-popup__designation">{prospect.designation}</div>
                                            </Tooltip>
                                        </div>
                                    </Link>
                                </div>
                            </div>
                            <ConditionalRender if={prospect.similarity}>
                                <div className='body-m-semibold map-popup__confidence self-start'>Confidence: <span className='body-m-semibold map-popup__confidence-value'>{prospect.similarity}%</span></div>
                            </ConditionalRender>
                        </div>
                        <div className="map-popup__suggestion-spacer"></div>
                        <SuggestionTags tags={prospect.suggestion_tags} />
                    </React.Fragment>)
            }) :
                <div className='body-m-semibold map-popup__empty'>
                    No suggestions found
                </div>
            }
            <div className="map-popup__divider-row flex items-center">
                <div className="map-popup__divider-line"></div>
                <div className='body-s-semibold map-popup__divider-label'>Or</div>
                <div className="map-popup__divider-line"></div>
            </div>
            <div>
                <SelectionControl
                    type="checkbox"
                    label="Search Prospects Manually"
                    checked={manualProspectEnabled}
                    onChange={() => setComponentState({ manualProspectEnabled: !manualProspectEnabled })}
                />
                <ConditionalRender if={manualProspectEnabled}>
                    <div className="map-popup__manual-search">
                        <AnMultipleSuggestionInputBox
                            inputClass="an_search_box"
                            placeholder="Search for prospects"
                            suggestionUrl="/prospects/search?team_search=true&only_unmapped=true"
                            selectedOptions={manualProspect}
                            callback={(val) => setComponentState({ manualProspect: val })}
                            style={{ margin: 'var(--space-m) 0 0' }}
                            theme="modern_selected"
                        />
                    </div>
                </ConditionalRender>
            </div>
            <div className="map-popup__divider-row flex items-center">
                <div className="map-popup__divider-line"></div>
                <div className='body-s-semibold map-popup__divider-label'>Or</div>
                <div className="map-popup__divider-line"></div>
            </div>
            <div className="map-popup__create-wrap">
                <SelectionControl
                    type="checkbox"
                    label="Create New Prospect"
                    checked={createNewProspect}
                    onChange={() => setComponentState({ createNewProspect: !createNewProspect })}
                />
            </div>
            <ConditionalRender if={componentData.errors?.postingJob}>
                <div className="body-s-semibold map-popup__error">{componentData.errors?.postingJob}</div>
            </ConditionalRender>
        </Popup>
    )
}

export default MapAndPostJobPopup
